import React, { useEffect, useMemo, useState } from 'react'
import {
    Card,
    Row,
    Col,
    Select,
    DatePicker,
    InputNumber,
    Input,
    Checkbox,
    Button,
    Radio,
    Table,
    Progress,
    Collapse,
    Tag,
    Typography,
    Space,
    Divider,
    Modal,
    Drawer,
    Descriptions,
    Spin,
    message
} from 'antd'
import { SearchOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import moment from 'moment'
import _ from 'lodash'
import { Scatter } from 'react-chartjs-2'
import * as api from '../api'

const PALETTE = [
    '#1f97a8',
    '#c98a39',
    '#8a7ff0',
    '#5ab86b',
    '#e0503a',
    '#d9a441',
    '#39b7c9',
    '#b86bb8',
    '#6b9be0',
    '#7a8b99'
]

const { Option } = Select
const { Panel } = Collapse
const { Text, Title } = Typography

const OFFER = process.env.REACT_APP_OFFER_ENDPOINT

const emptyRoom = () => ({ adults: 2, children: [], infants: 0 })

// Human-readable prebook result: price/room/cancellation summary, raw JSON below.
function PrebookResult({ result, offerId }) {
    const ok = result.success !== false
    const d = result.data || {}
    const prebookRooms = d.rooms || []
    const cans = d.cancellations || []
    return (
        <>
            <div style={{ marginBottom: 12 }}>
                {ok ? <Tag color="green">PREBOOK OK</Tag> : <Tag color="red">FAILED</Tag>}
                {d.non_refundable != null &&
                    (d.non_refundable ? (
                        <Tag color="orange">non-refundable</Tag>
                    ) : (
                        <Tag color="green">
                            {d.free_cancellation_deadline
                                ? `free cancel till ${String(d.free_cancellation_deadline).slice(0, 16)}`
                                : 'refundable'}
                        </Tag>
                    ))}
            </div>
            {!ok && (
                <Text type="danger">{result.message || 'Prebook failed'}</Text>
            )}
            {ok && (
                <>
                    <Descriptions size="small" column={2} bordered>
                        <Descriptions.Item label="Total price">
                            <Text strong>
                                {Number(d.amount).toLocaleString()} {d.currency}
                            </Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Supplier price">
                            {Number(d.supplier_amount).toLocaleString()} {d.supplier_currency}
                        </Descriptions.Item>
                        <Descriptions.Item label="Expires at" span={2}>
                            {d.expired_at
                                ? moment(d.expired_at).format('YYYY-MM-DD HH:mm:ss')
                                : '—'}
                        </Descriptions.Item>
                    </Descriptions>
                    {prebookRooms.map((r, i) => (
                        <Descriptions
                            key={i}
                            size="small"
                            column={2}
                            bordered
                            style={{ marginTop: 10 }}
                            title={
                                <Space>
                                    <Tag color="blue">Room {r.idx || i + 1}</Tag>
                                    <Text strong>{r.room_name}</Text>
                                </Space>
                            }
                        >
                            <Descriptions.Item label="Board">
                                {r.board_basis || '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Price">
                                {Number(r.price).toLocaleString()} {r.currency}
                                {r.tax ? `  (tax ${r.tax})` : ''}
                            </Descriptions.Item>
                            <Descriptions.Item label="Remaining">
                                {r.remainings != null ? r.remainings : '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Room type">
                                <Text code>{r.room_type_id}</Text>
                            </Descriptions.Item>
                            {r.remarks && r.remarks.length > 0 && (
                                <Descriptions.Item label="Remarks" span={2}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {r.remarks.join(' ')}
                                    </Text>
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    ))}
                    {cans.length > 0 && (
                        <Table
                            size="small"
                            style={{ marginTop: 10 }}
                            pagination={false}
                            rowKey={(c, i) => i}
                            dataSource={cans}
                            columns={[
                                { title: 'From', dataIndex: 'from', width: 170 },
                                { title: 'To', dataIndex: 'to', width: 170 },
                                {
                                    title: 'Penalty',
                                    render: c => (
                                        <Text strong={c.amount > 0}>
                                            {Number(c.amount).toLocaleString()} {d.currency}
                                        </Text>
                                    )
                                },
                                { title: 'TZ', dataIndex: 'timezone', width: 70 }
                            ]}
                        />
                    )}
                </>
            )}
            <Collapse ghost style={{ marginTop: 10 }}>
                <Collapse.Panel header="Raw response" key="raw">
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {`offer_id: ${offerId}`}
                    </Text>
                    <pre style={{ maxHeight: 320, overflow: 'auto' }}>
                        {JSON.stringify(result, null, 2)}
                    </pre>
                </Collapse.Panel>
            </Collapse>
        </>
    )
}

// Price distribution across suppliers: floating min→max range bars + a scatter
// of every single rate, so outliers and clustering are visible at a glance.
function PriceCharts({ offers }) {
    const stats = useMemo(() => {
        const bySup = _.groupBy(offers, 'supplier_code')
        return Object.entries(bySup)
            .map(([sup, list], i) => {
                const prices = list.map(o => Number(o.price)).filter(p => !Number.isNaN(p))
                return {
                    sup,
                    color: PALETTE[i % PALETTE.length],
                    n: prices.length,
                    min: _.min(prices),
                    max: _.max(prices),
                    avg: _.mean(prices),
                    prices
                }
            })
            .sort((a, b) => a.min - b.min)
    }, [offers])

    if (!stats.length) return null
    const currency = (offers[0] && offers[0].currency) || ''

    // one dot per rate, one row per supplier
    const scatterData = {
        datasets: stats.map((s, i) => ({
            label: `${s.sup} (${s.n})`,
            data: s.prices.map(p => ({ x: p, y: i })),
            backgroundColor: `${s.color}bb`,
            borderColor: s.color,
            pointRadius: 3.5,
            pointHoverRadius: 6
        }))
    }
    const scatterOptions = {
        legend: { display: false },
        maintainAspectRatio: false,
        tooltips: {
            callbacks: {
                title: item => (item && item[0] ? stats[item[0].datasetIndex].sup : ''),
                label: item => {
                    const s = stats[item.datasetIndex]
                    return ` ${Number(item.xLabel).toLocaleString()} ${currency}  (min ${s.min.toLocaleString()} · avg ${Math.round(s.avg).toLocaleString()} · max ${s.max.toLocaleString()})`
                }
            }
        },
        scales: {
            xAxes: [
                {
                    ticks: { callback: v => v.toLocaleString() },
                    scaleLabel: { display: true, labelString: `price (${currency})` },
                    gridLines: { color: 'rgba(0,0,0,0.05)' }
                }
            ],
            yAxes: [
                {
                    ticks: {
                        min: -0.5,
                        max: stats.length - 0.5,
                        stepSize: 1,
                        callback: v =>
                            Number.isInteger(v) && v >= 0 && v < stats.length
                                ? `${stats[v].sup} (${stats[v].n})`
                                : ''
                    },
                    gridLines: { color: 'rgba(0,0,0,0.05)' }
                }
            ]
        }
    }

    return (
        <Collapse defaultActiveKey={['dist']} ghost style={{ marginBottom: 8 }}>
            <Collapse.Panel header="Price distribution by supplier" key="dist">
                <div style={{ height: Math.max(150, stats.length * 44 + 70) }}>
                    <Scatter data={scatterData} options={scatterOptions} />
                </div>
            </Collapse.Panel>
        </Collapse>
    )
}

// --- persist the whole form to the URL hash query so F5 keeps everything ---
function parseParams() {
    try {
        const hash = window.location.hash || ''
        const qi = hash.indexOf('?')
        if (qi < 0) return {}
        const sp = new URLSearchParams(hash.slice(qi + 1))
        const num = v => (v != null && v !== '' ? Number(v) : undefined)
        const list = v => (v ? v.split(',').filter(Boolean) : undefined)
        let rooms
        try {
            rooms = sp.get('rooms') ? JSON.parse(sp.get('rooms')) : undefined
        } catch (e) {
            rooms = undefined
        }
        return {
            user: num(sp.get('user')),
            suppliers: (list(sp.get('suppliers')) || []).map(Number),
            mode: sp.get('mode') || undefined,
            region: sp.get('region') || undefined,
            airport: sp.get('airport') || undefined,
            hotels: list(sp.get('hotels')),
            checkin: sp.get('checkin') || undefined,
            checkout: sp.get('checkout') || undefined,
            nat: sp.get('nat') || undefined,
            cur: sp.get('cur') || undefined,
            pkg: sp.get('pkg') === '1' ? true : sp.get('pkg') === '0' ? false : undefined,
            wait: num(sp.get('wait')),
            rooms
        }
    } catch (e) {
        return {}
    }
}

function writeParams(state) {
    try {
        const sp = new URLSearchParams()
        const set = (k, v) => {
            if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) return
            sp.set(k, Array.isArray(v) ? v.join(',') : `${v}`)
        }
        set('user', state.userId)
        set('suppliers', state.supplierIds)
        set('mode', state.targetMode)
        if (state.targetMode === 'region') set('region', state.regionId)
        if (state.targetMode === 'airport') set('airport', state.airportCode)
        if (state.targetMode === 'hotels') set('hotels', state.hotelIds)
        if (state.range && state.range[0]) set('checkin', state.range[0].format('YYYY-MM-DD'))
        if (state.range && state.range[1]) set('checkout', state.range[1].format('YYYY-MM-DD'))
        set('nat', state.nationality)
        set('cur', state.currency)
        set('pkg', state.pkg ? '1' : '0')
        set('wait', state.waitTime)
        set('rooms', JSON.stringify(state.rooms))
        const hash = window.location.hash || '#/'
        const path = hash.replace(/^#/, '').split('?')[0]
        window.history.replaceState(null, '', `#${path}?${sp.toString()}`)
    } catch (e) {
        /* ignore */
    }
}

export default function HotelReproduce() {
    // --- reference data ---
    const init = useMemo(parseParams, [])

    const [users, setUsers] = useState([])
    const [loadingUsers, setLoadingUsers] = useState(false)
    const [userId, setUserId] = useState(init.user)
    const [suppliers, setSuppliers] = useState([])
    const [loadingSuppliers, setLoadingSuppliers] = useState(false)
    const [supplierIds, setSupplierIds] = useState(init.suppliers || [])

    // --- target ---
    const [targetMode, setTargetMode] = useState(init.mode || 'region')
    const [regionOpts, setRegionOpts] = useState(
        init.region
            ? [{ region_id: init.region, region_name_full: init.region, region_type: '' }]
            : []
    )
    const [regionId, setRegionId] = useState(init.region)
    const [airportOpts, setAirportOpts] = useState(
        init.airport
            ? [{ airport_code: init.airport, airport_name: '', country_code: '' }]
            : []
    )
    const [airportCode, setAirportCode] = useState(init.airport)
    const [hotelOpts, setHotelOpts] = useState(
        (init.hotels || []).map(id => ({ hotel_id: id, name: `#${id}` }))
    )
    const [hotelIds, setHotelIds] = useState(init.hotels || [])
    const [searching, setSearching] = useState(false)

    // --- search params ---
    const [range, setRange] = useState(
        init.checkin && init.checkout
            ? [moment(init.checkin), moment(init.checkout)]
            : [moment().add(30, 'day'), moment().add(33, 'day')]
    )
    const [rooms, setRooms] = useState(
        init.rooms && init.rooms.length ? init.rooms : [emptyRoom()]
    )
    const [nationality, setNationality] = useState(init.nat || 'US')
    const [currency, setCurrency] = useState(init.cur || '')
    const [pkg, setPkg] = useState(init.pkg || false)
    const [waitTime, setWaitTime] = useState(init.wait || 20000)

    // --- run state ---
    const [running, setRunning] = useState(false)
    const [progress, setProgress] = useState(0)
    const [result, setResult] = useState(null)
    const [builtReq, setBuiltReq] = useState(null)
    const [builtUrl, setBuiltUrl] = useState('')
    const [curl, setCurl] = useState('')

    // auth reused for per-hotel mapping lookups
    const [authRef, setAuthRef] = useState(null)
    const [mapState, setMapState] = useState({
        open: false,
        loading: false,
        hotel: null,
        rows: [],
        total: 0
    })
    // getrooms drawer (offers for one hotel) + prebook
    const [detail, setDetail] = useState({
        open: false,
        loading: false,
        hotel: null,
        reqId: null,
        offers: [],
        error: null
    })
    const [prebookState, setPrebookState] = useState({
        open: false,
        loading: false,
        offerId: null,
        result: null
    })

    useEffect(() => {
        setLoadingUsers(true)
        api.getUsers()
            .then(setUsers)
            .catch(e => message.error(`Load users failed: ${e.message}`))
            .finally(() => setLoadingUsers(false))
        // restore suppliers list if a user was preset from the URL
        if (init.user) {
            setLoadingSuppliers(true)
            api.getUserSuppliers(init.user)
                .then(setSuppliers)
                .catch(() => {})
                .finally(() => setLoadingSuppliers(false))
        }
    }, [])

    // keep the URL hash query in sync with the form so F5 restores it
    useEffect(() => {
        writeParams({
            userId,
            supplierIds,
            targetMode,
            regionId,
            airportCode,
            hotelIds,
            range,
            rooms,
            nationality,
            currency,
            pkg,
            waitTime
        })
    }, [
        userId,
        supplierIds,
        targetMode,
        regionId,
        airportCode,
        hotelIds,
        range,
        rooms,
        nationality,
        currency,
        pkg,
        waitTime
    ])

    const onUserChange = uid => {
        setUserId(uid)
        setSupplierIds([])
        setSuppliers([])
        if (!uid) return
        setLoadingSuppliers(true)
        api.getUserSuppliers(uid)
            .then(setSuppliers)
            .catch(e => message.error(`Load suppliers failed: ${e.message}`))
            .finally(() => setLoadingSuppliers(false))
    }

    // debounced async pickers
    const searchRegions = useMemo(
        () =>
            _.debounce(q => {
                if (!q || q.length < 2) return
                api.searchRegions(q).then(setRegionOpts).catch(() => setRegionOpts([]))
            }, 350),
        []
    )
    const searchAirports = useMemo(
        () =>
            _.debounce(q => {
                if (!q || q.length < 2) return
                api.searchAirports(q).then(setAirportOpts).catch(() => setAirportOpts([]))
            }, 350),
        []
    )
    const searchHotels = useMemo(
        () =>
            _.debounce(q => {
                if (!q || q.length < 2) return
                setSearching(true)
                api.searchHotelsByName(q)
                    .then(setHotelOpts)
                    .catch(() => setHotelOpts([]))
                    .finally(() => setSearching(false))
            }, 350),
        []
    )

    // rooms editor
    const setRoom = (idx, patch) =>
        setRooms(rs => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
    const addRoom = () => setRooms(rs => [...rs, emptyRoom()])
    const removeRoom = idx => setRooms(rs => (rs.length > 1 ? rs.filter((_r, i) => i !== idx) : rs))

    const validate = () => {
        if (!userId) return 'Pick a user/switch'
        if (!range || !range[0] || !range[1]) return 'Pick check-in / check-out'
        if (!rooms.length) return 'Add at least one room'
        if (targetMode === 'region' && !regionId) return 'Pick a region'
        if (targetMode === 'airport' && !airportCode) return 'Pick an airport'
        if (targetMode === 'hotels' && !hotelIds.length) return 'Pick at least one hotel'
        if (targetMode === 'hotels' && hotelIds.length > 50) return 'Max 50 hotels for search-by-id'
        return null
    }

    const baseBody = () => {
        const body = {
            checkin: range[0].format('YYYY-MM-DD'),
            checkout: range[1].format('YYYY-MM-DD'),
            rooms: rooms.map((r, idx) => ({
                idx: idx + 1,
                adults: r.adults,
                children: (r.children || []).map(age => ({ age: Number(age) })),
                infants: _.times(r.infants || 0, () => ({ age: 1 }))
            })),
            package: pkg,
            language_code: 'en-US',
            ip: '127.0.0.1',
            wait_time: waitTime,
            user_agent: (window.navigator && window.navigator.userAgent) || 'gateboard',
            session_id: 'gateboard-reproduce',
            nationality: (nationality || 'US').toUpperCase(),
            country_residence: (nationality || 'US').toUpperCase()
        }
        if (supplierIds.length) body.supplier_ids = supplierIds
        if (currency) body.currency = currency.toUpperCase()
        return body
    }

    const buildBody = () => {
        const body = baseBody()
        let url
        if (targetMode === 'region') {
            body.region_id = regionId
            url = `${OFFER}/api/v3/search`
        } else if (targetMode === 'airport') {
            body.destination_code = airportCode
            url = `${OFFER}/api/v3/search`
        } else {
            body.ids = hotelIds.map(String)
            url = `${OFFER}/api/v3/search-by-id`
        }
        return { url, body }
    }

    // trace-id / booking url → prefill the whole form from a real prod search
    const [traceInput, setTraceInput] = useState('')
    const [tracing, setTracing] = useState(false)
    // last-resort: decode the shopping link's own query params and fill what we can
    const fillFromUrlParams = raw => {
        try {
            const u = new URL(raw.trim())
            const exp = JSON.parse(u.searchParams.get('expectation') || '{}')
            const travelers = JSON.parse(u.searchParams.get('travelers') || '[]')
            let filled = 0
            if (exp.ht_checkin_date && exp.ht_checkout_date) {
                setRange([moment(exp.ht_checkin_date), moment(exp.ht_checkout_date)])
                filled += 1
            }
            if (travelers.length) {
                const byRoom = _.groupBy(travelers, t => t.room || 1)
                setRooms(
                    Object.values(byRoom).map(list => ({
                        adults: list.filter(t => t.type === 'adult').length || 1,
                        children: list
                            .filter(t => t.type === 'child')
                            .map(t => `${t.age}`),
                        infants: list.filter(t => t.type === 'infant').length
                    }))
                )
                filled += 1
            }
            if (exp.nationality) {
                setNationality(String(exp.nationality).toUpperCase())
                filled += 1
            }
            const cur = u.searchParams.get('currency_code')
            if (cur) {
                setCurrency(cur.toUpperCase())
                filled += 1
            }
            if (filled) {
                message.info(
                    `Trace lookup failed — filled ${filled} field group(s) from the URL params instead. Pick user + target manually.`,
                    8
                )
            }
            return filled > 0
        } catch (e) {
            return false
        }
    }

    const fillFromTrace = async () => {
        if (!traceInput.trim()) return message.warning('Paste a trace-id or a URL containing one')
        setTracing(true)
        try {
            const { payload, email } = await api.traceToPayload('hotel', traceInput, 'v3')
            if (email) {
                const u = users.find(x => x.email === email)
                if (u) onUserChange(u.id)
                else message.warning(`User ${email} not in the list — pick one manually`)
            }
            if (payload.checkin && payload.checkout) {
                setRange([moment(payload.checkin), moment(payload.checkout)])
            }
            if (Array.isArray(payload.rooms) && payload.rooms.length) {
                setRooms(
                    payload.rooms.map(r => ({
                        adults: r.adults || 1,
                        children: (r.children || []).map(c => `${c.age}`),
                        infants: (r.infants || []).length
                    }))
                )
            }
            if (payload.nationality) setNationality(String(payload.nationality).toUpperCase())
            if (payload.currency) setCurrency(String(payload.currency).toUpperCase())
            if (payload.region_id) {
                setTargetMode('region')
                setRegionOpts([
                    { region_id: payload.region_id, region_name_full: payload.region_id, region_type: '' }
                ])
                setRegionId(payload.region_id)
            } else if (payload.destination_code) {
                setTargetMode('airport')
                setAirportOpts([
                    { airport_code: payload.destination_code, airport_name: '', country_code: '' }
                ])
                setAirportCode(payload.destination_code)
            } else if (Array.isArray(payload.ids) && payload.ids.length) {
                setTargetMode('hotels')
                setHotelOpts(payload.ids.map(id => ({ hotel_id: id, name: `#${id}` })))
                setHotelIds(payload.ids.map(String))
            }
            message.success('Form filled from trace — review and Run')
        } catch (e) {
            // trace lookup failed (e.g. no logs_search row) → best-effort fill
            // straight from the pasted URL's own query params
            const salvaged =
                /^https?:\/\//.test(traceInput.trim()) && fillFromUrlParams(traceInput)
            if (!salvaged) {
                message.error(
                    `Trace fill failed: ${_.get(e, 'response.data.message') || e.message}`,
                    10
                )
            }
        } finally {
            setTracing(false)
        }
    }

    const run = async () => {
        const err = validate()
        if (err) {
            message.warning(err)
            return
        }
        setRunning(true)
        setProgress(0)
        setResult(null)
        try {
            const user = users.find(u => u.id === userId)
            const token = await api.genToken(user.id, user.email)
            setAuthRef({ token, email: user.email })
            const { url, body } = buildBody()
            // short per-poll server wait so each poll returns fast with partial
            // data (progressive UI); the user's wait_time is the TOTAL budget
            body.wait_time = Math.min(5000, waitTime)
            setBuiltReq(body)
            setBuiltUrl(url)
            setCurl(
                `curl -X POST '${url}' \\\n  -H 'Content-Type: application/json' \\\n  -H 'x-key: ${user.email}' \\\n  -H 'x-access-token: ${token}' \\\n  -d '${JSON.stringify(body)}'`
            )
            const headers = {
                'Content-Type': 'application/json',
                'x-key': user.email,
                'x-access-token': token
            }
            const res = await api.runSearch({
                url,
                headers,
                body,
                maxWaitMs: Math.max(waitTime + 15000, 45000),
                // progressive: render whatever hotels/rates each poll already returned
                onTick: ({ progress: p, res: tick }) => {
                    setProgress(Math.round((p || 0) * 100))
                    if (tick && Array.isArray(tick.data)) setResult(tick)
                }
            })
            setProgress(100)
            setResult(res)
            const n = (res.data || []).length
            message.success(`Done — ${n} hotel(s), progress ${Math.round((res.progress || 0) * 100)}%`)
        } catch (e) {
            const msg = _.get(e, 'response.data.message') || e.message
            message.error(`Search failed: ${msg}`, 12)
            setResult(_.get(e, 'response.data') || { error: msg })
        } finally {
            setRunning(false)
        }
    }

    const openMappings = async row => {
        if (!authRef) {
            message.warning('Run a search first (need a token)')
            return
        }
        setMapState({ open: true, loading: true, hotel: row.easygds_id, rows: [], total: 0 })
        try {
            const res = await api.getHotelMappings({ easygdsId: row.easygds_id, ...authRef })
            const first = (res.data && res.data[0]) || {}
            const mapRows = Object.entries(first)
                .filter(([, v]) => v != null && v !== '')
                .map(([supplier, id]) => ({ supplier, id }))
            setMapState({
                open: true,
                loading: false,
                hotel: row.easygds_id,
                rows: mapRows,
                total: mapRows.length
            })
        } catch (e) {
            setMapState(s => ({ ...s, loading: false }))
            message.error(
                `Load mappings failed: ${_.get(e, 'response.data.message') || e.message}`
            )
        }
    }

    // click a hotel → getrooms (full bookable offers for that one hotel, via search-by-id)
    const openDetail = async row => {
        if (!authRef) {
            message.warning('Run a search first (need a token)')
            return
        }
        setDetail({
            open: true,
            loading: true,
            hotel: row.easygds_id,
            reqId: null,
            offers: [],
            error: null
        })
        try {
            const body = baseBody()
            body.ids = [String(row.easygds_id)]
            body.wait_time = Math.min(4000, waitTime) // per-poll wait → progressive offers
            const toOffers = res2 =>
                (res2.data || []).flatMap(d =>
                    (d.offers || []).map(o => ({
                        ...o,
                        supplier_code: d.supplier_code,
                        hotel_id: d.hotel_id,
                        source_id: d.source_id
                    }))
                )
            const res = await api.runSearch({
                url: `${OFFER}/api/v3/search-by-id`,
                headers: {
                    'Content-Type': 'application/json',
                    'x-key': authRef.email,
                    'x-access-token': authRef.token
                },
                body,
                maxWaitMs: Math.max(waitTime + 15000, 45000),
                // progressive: show offers as each supplier answers
                onTick: ({ res: tick }) => {
                    if (tick && Array.isArray(tick.data) && tick.data.length) {
                        setDetail(s => ({
                            ...s,
                            reqId: tick.request_id || s.reqId,
                            offers: toOffers(tick)
                        }))
                    }
                }
            })
            const offers = toOffers(res)
            setDetail({
                open: true,
                loading: false,
                hotel: row.easygds_id,
                reqId: res.request_id,
                offers,
                error: null
            })
        } catch (e) {
            const msg = _.get(e, 'response.data.message') || e.message
            setDetail(s => ({ ...s, loading: false, error: msg }))
            message.error(`Get rooms failed: ${msg}`, 10)
        }
    }

    const doPrebook = async offer => {
        setPrebookState({ open: true, loading: true, offerId: offer.id, result: null })
        try {
            const result = await api.prebook({
                requestId: detail.reqId,
                offerId: offer.id,
                token: authRef.token,
                email: authRef.email
            })
            setPrebookState({ open: true, loading: false, offerId: offer.id, result })
            if (result && result.success !== false) message.success('Prebook OK')
        } catch (e) {
            const msg = _.get(e, 'response.data.message') || e.message
            setPrebookState({
                open: true,
                loading: false,
                offerId: offer.id,
                result: _.get(e, 'response.data') || { success: false, message: msg }
            })
            message.error(`Prebook failed: ${msg}`, 10)
        }
    }

    const rows = useMemo(() => {
        const data = (result && result.data) || []
        return data.map((h, i) => {
            const offer = h.offer || (h.offers && _.minBy(h.offers, o => Number(o.price)))
            return {
                key: `${h.easygds_id || h.hotel_id || i}-${i}`,
                easygds_id: h.easygds_id || h.hotel_id,
                supplier_code: h.supplier_code,
                refundable: h.refundable,
                price: offer ? offer.price : null,
                currency: offer ? offer.currency : '',
                rooms: offer && offer.rooms ? offer.rooms.map(r => r.room_name).join(' + ') : ''
            }
        })
    }, [result])

    const columns = [
        {
            title: 'easygds_id',
            dataIndex: 'easygds_id',
            width: 120,
            render: (v, r) => (
                <a
                    onClick={e => {
                        e.stopPropagation()
                        openDetail(r)
                    }}
                    style={{ fontWeight: 500 }}
                >
                    {v}
                </a>
            )
        },
        {
            title: 'Supplier',
            dataIndex: 'supplier_code',
            width: 130,
            filters: _.uniq(rows.map(r => r.supplier_code))
                .filter(Boolean)
                .map(s => ({ text: s, value: s })),
            onFilter: (v, r) => r.supplier_code === v,
            render: c => <Tag>{c}</Tag>
        },
        {
            title: 'Cheapest',
            key: 'price',
            width: 140,
            sorter: (a, b) =>
                (a.price == null ? Infinity : a.price) - (b.price == null ? Infinity : b.price),
            defaultSortOrder: 'ascend',
            render: r =>
                r.price != null ? (
                    <Text strong>
                        {Number(r.price).toLocaleString()} {r.currency}
                    </Text>
                ) : (
                    <Text type="secondary">—</Text>
                )
        },
        {
            title: 'Refundable',
            dataIndex: 'refundable',
            width: 110,
            filters: [
                { text: 'refundable', value: true },
                { text: 'non-ref', value: false }
            ],
            onFilter: (v, r) => !!r.refundable === v,
            render: v =>
                v ? <Tag color="green">refundable</Tag> : <Tag color="orange">non-ref</Tag>
        },
        { title: 'Rooms', dataIndex: 'rooms', ellipsis: true },
        {
            title: 'Mappings',
            key: 'map',
            width: 96,
            render: r => (
                <Button
                    size="small"
                    onClick={e => {
                        e.stopPropagation()
                        openMappings(r)
                    }}
                >
                    View
                </Button>
            )
        }
    ]

    return (
        <div style={{ padding: 16 }}>
            <Title level={4}>Hotel Search Reproduce</Title>
            <Text type="secondary">
                Reproduce a V3 hotel search on offer-worker without Postman — pick a switch, suppliers,
                dates, rooms and a target (region / airport / hotels), then fire it.
            </Text>

            <Card size="small" style={{ marginTop: 16 }}>
                <Row gutter={8} style={{ marginBottom: 12 }}>
                    <Col flex="auto">
                        <Input
                            placeholder="Prefill from a prod search: paste a trace-id (or any URL/text containing it)"
                            value={traceInput}
                            onChange={e => setTraceInput(e.target.value)}
                            onPressEnter={fillFromTrace}
                            allowClear
                        />
                    </Col>
                    <Col>
                        <Button loading={tracing} onClick={fillFromTrace}>
                            Fill from trace
                        </Button>
                    </Col>
                </Row>
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                        <label>User / switch</label>
                        <Select
                            style={{ width: '100%' }}
                            placeholder="Select a switch"
                            loading={loadingUsers}
                            showSearch
                            optionFilterProp="children"
                            value={userId}
                            onChange={onUserChange}
                        >
                            {users.map(u => (
                                <Option key={u.id} value={u.id}>
                                    {(u.name || u.email) + ' — ' + u.email}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} md={12}>
                        <label>Suppliers (empty = all enabled)</label>
                        <Select
                            mode="multiple"
                            style={{ width: '100%' }}
                            placeholder={userId ? 'All enabled suppliers' : 'Pick a user first'}
                            loading={loadingSuppliers}
                            disabled={!userId}
                            value={supplierIds}
                            onChange={setSupplierIds}
                            optionFilterProp="children"
                        >
                            {suppliers.map(s => (
                                <Option key={s.id} value={s.id}>
                                    {`${s.code}${s.name ? ` · ${s.name}` : ''}${
                                        s.source_id ? ` (${s.source_id})` : ''
                                    }`}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} md={12}>
                        <label>Target</label>
                        <div>
                            <Radio.Group
                                value={targetMode}
                                onChange={e => setTargetMode(e.target.value)}
                                optionType="button"
                                buttonStyle="solid"
                                style={{ marginBottom: 8 }}
                            >
                                <Radio.Button value="region">Region</Radio.Button>
                                <Radio.Button value="airport">Airport</Radio.Button>
                                <Radio.Button value="hotels">Hotels</Radio.Button>
                            </Radio.Group>
                            {targetMode === 'region' && (
                                <Select
                                    style={{ width: '100%' }}
                                    showSearch
                                    placeholder="Search region name or region_id"
                                    filterOption={false}
                                    onSearch={searchRegions}
                                    value={regionId}
                                    onChange={setRegionId}
                                    notFoundContent={null}
                                >
                                    {regionOpts.map(r => (
                                        <Option key={r.region_id} value={r.region_id}>
                                            {`${r.region_name_full || r.region_name} · ${r.region_type} [${r.region_id}]`}
                                        </Option>
                                    ))}
                                </Select>
                            )}
                            {targetMode === 'airport' && (
                                <Select
                                    style={{ width: '100%' }}
                                    showSearch
                                    placeholder="Search airport code or name (→ destination_code)"
                                    filterOption={false}
                                    onSearch={searchAirports}
                                    value={airportCode}
                                    onChange={setAirportCode}
                                    notFoundContent={null}
                                >
                                    {airportOpts.map(a => (
                                        <Option key={a.airport_code} value={a.airport_code}>
                                            {`${a.airport_code} · ${a.airport_name} (${a.country_code})`}
                                        </Option>
                                    ))}
                                </Select>
                            )}
                            {targetMode === 'hotels' && (
                                <Select
                                    mode="multiple"
                                    style={{ width: '100%' }}
                                    showSearch
                                    placeholder="Search hotel name (max 50)"
                                    filterOption={false}
                                    onSearch={searchHotels}
                                    loading={searching}
                                    value={hotelIds}
                                    onChange={setHotelIds}
                                    notFoundContent={null}
                                >
                                    {hotelOpts.map(h => (
                                        <Option key={h.hotel_id} value={h.hotel_id}>
                                            {`${h.name}${h.city ? ` · ${h.city}` : ''} #${h.hotel_id}`}
                                        </Option>
                                    ))}
                                </Select>
                            )}
                        </div>
                    </Col>

                    <Col xs={24} md={12}>
                        <label>Check-in / Check-out</label>
                        <DatePicker.RangePicker
                            style={{ width: '100%' }}
                            value={range}
                            onChange={setRange}
                            disabledDate={d => d && d < moment().startOf('day')}
                        />
                        <Row gutter={8} style={{ marginTop: 8 }}>
                            <Col span={8}>
                                <label>Nationality</label>
                                <Input
                                    maxLength={2}
                                    value={nationality}
                                    onChange={e => setNationality(e.target.value.toUpperCase())}
                                />
                            </Col>
                            <Col span={8}>
                                <label>Currency</label>
                                <Input
                                    placeholder="user default"
                                    maxLength={3}
                                    value={currency}
                                    onChange={e => setCurrency(e.target.value.toUpperCase())}
                                />
                            </Col>
                            <Col span={8}>
                                <label>wait_time (ms)</label>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={1000}
                                    max={100000}
                                    step={1000}
                                    value={waitTime}
                                    onChange={setWaitTime}
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>

                <Divider style={{ margin: '12px 0' }} orientation="left">
                    Rooms
                </Divider>
                <div style={{ maxWidth: 620 }}>
                    <Row gutter={8} style={{ marginBottom: 6 }}>
                        <Col flex="72px" />
                        <Col flex="90px">
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Adults
                            </Text>
                        </Col>
                        <Col flex="auto">
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Children ages
                            </Text>
                        </Col>
                        <Col flex="96px">
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Infants
                            </Text>
                        </Col>
                        <Col flex="32px" />
                    </Row>
                    {rooms.map((r, idx) => (
                        <Row
                            key={idx}
                            gutter={8}
                            align="middle"
                            style={{ marginBottom: 8 }}
                        >
                            <Col flex="72px">
                                <Tag color="blue" style={{ margin: 0 }}>
                                    Room {idx + 1}
                                </Tag>
                            </Col>
                            <Col flex="90px">
                                <InputNumber
                                    min={1}
                                    max={10}
                                    style={{ width: '100%' }}
                                    value={r.adults}
                                    onChange={v => setRoom(idx, { adults: v })}
                                />
                            </Col>
                            <Col flex="auto">
                                <Select
                                    mode="tags"
                                    style={{ width: '100%' }}
                                    placeholder="e.g. 5, 8"
                                    value={r.children}
                                    onChange={v =>
                                        setRoom(idx, {
                                            children: v.filter(x => /^\d{1,2}$/.test(x))
                                        })
                                    }
                                    tokenSeparators={[',', ' ']}
                                />
                            </Col>
                            <Col flex="96px">
                                <InputNumber
                                    min={0}
                                    max={4}
                                    style={{ width: '100%' }}
                                    value={r.infants}
                                    onChange={v => setRoom(idx, { infants: v })}
                                />
                            </Col>
                            <Col flex="32px">
                                <Button
                                    danger
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    disabled={rooms.length === 1}
                                    onClick={() => removeRoom(idx)}
                                />
                            </Col>
                        </Row>
                    ))}
                    <Button type="dashed" icon={<PlusOutlined />} onClick={addRoom}>
                        Add room
                    </Button>
                </div>

                <Divider style={{ margin: '12px 0' }} />
                <Space>
                    <Checkbox checked={pkg} onChange={e => setPkg(e.target.checked)}>
                        Package
                    </Checkbox>
                    <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        loading={running}
                        onClick={run}
                    >
                        Run search
                    </Button>
                </Space>
            </Card>

            {(running || result) && (
                <Card size="small" style={{ marginTop: 16 }} title="Result">
                    <Progress
                        percent={progress}
                        status={running ? 'active' : progress >= 100 ? 'success' : 'normal'}
                    />
                    {result && (
                        <>
                            <div style={{ margin: '8px 0' }}>
                                <Space split={<Divider type="vertical" />}>
                                    <Text>
                                        Hotels: <Text strong>{(result.data || []).length}</Text>
                                    </Text>
                                    <Text>
                                        progress:{' '}
                                        <Text strong>{Math.round((result.progress || 0) * 100)}%</Text>
                                    </Text>
                                    {result.request_id && (
                                        <Text copyable={{ text: result.request_id }}>
                                            request_id: <Text code>{result.request_id}</Text>
                                        </Text>
                                    )}
                                    {result.attempts && <Text>polls: {result.attempts}</Text>}
                                </Space>
                            </div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Click a hotel row → GetRooms (bookable offers) → Prebook.
                            </Text>
                            {rows.filter(r => r.price != null).length > 1 && (
                                <PriceCharts offers={rows.filter(r => r.price != null)} />
                            )}
                            <Table
                                size="small"
                                columns={columns}
                                dataSource={rows}
                                pagination={{ pageSize: 20, showSizeChanger: true }}
                                scroll={{ x: true }}
                                onRow={record => ({
                                    onClick: () => openDetail(record),
                                    style: { cursor: 'pointer' }
                                })}
                            />
                            <Collapse ghost style={{ marginTop: 8 }}>
                                <Panel header="Request payload" key="req">
                                    <pre style={{ maxHeight: 260, overflow: 'auto' }}>
                                        {JSON.stringify(builtReq, null, 2)}
                                    </pre>
                                    <Text type="secondary">POST {builtUrl}</Text>
                                </Panel>
                                <Panel header="curl" key="curl">
                                    <Typography.Paragraph
                                        copyable={{ text: curl }}
                                        style={{ marginBottom: 4 }}
                                    >
                                        <Text type="secondary">Copy curl</Text>
                                    </Typography.Paragraph>
                                    <pre style={{ maxHeight: 220, overflow: 'auto' }}>{curl}</pre>
                                </Panel>
                                <Panel header="Raw response" key="res">
                                    <pre style={{ maxHeight: 320, overflow: 'auto' }}>
                                        {JSON.stringify(result, null, 2)}
                                    </pre>
                                </Panel>
                            </Collapse>
                        </>
                    )}
                </Card>
            )}

            <Modal
                visible={mapState.open}
                title={`Hotel mappings — easygds_id ${mapState.hotel || ''}`}
                footer={null}
                width={540}
                onCancel={() => setMapState(s => ({ ...s, open: false }))}
            >
                <Spin spinning={mapState.loading}>
                    <div style={{ marginBottom: 8 }}>
                        <Text type="secondary">
                            {mapState.total} supplier/system mapping(s) for this hotel
                        </Text>
                    </div>
                    <Table
                        size="small"
                        pagination={false}
                        scroll={{ y: 360 }}
                        dataSource={mapState.rows.map((r, i) => ({ key: i, ...r }))}
                        columns={[
                            {
                                title: 'Supplier / System',
                                dataIndex: 'supplier',
                                width: 200,
                                render: s => <Tag>{s}</Tag>
                            },
                            {
                                title: 'Mapped hotel id',
                                dataIndex: 'id',
                                render: v => (
                                    <Text code copyable>
                                        {`${v}`}
                                    </Text>
                                )
                            }
                        ]}
                    />
                </Spin>
            </Modal>

            <Drawer
                title={`GetRooms — easygds_id ${detail.hotel || ''}`}
                placement="right"
                width={940}
                visible={detail.open}
                onClose={() => setDetail(s => ({ ...s, open: false }))}
            >
                <Spin spinning={detail.loading}>
                    {detail.error && <Text type="danger">{detail.error}</Text>}
                    {!detail.error && (
                        <>
                            <div style={{ marginBottom: 8 }}>
                                <Space split={<Divider type="vertical" />}>
                                    <Text>
                                        <Text strong>{detail.offers.length}</Text> offer(s)
                                    </Text>
                                    {detail.reqId && (
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            request_id{' '}
                                            <Text code copyable={{ text: detail.reqId }}>
                                                {`${detail.reqId.slice(0, 12)}…`}
                                            </Text>
                                        </Text>
                                    )}
                                </Space>
                            </div>
                            {detail.offers.length > 0 && (
                                <PriceCharts offers={detail.offers} />
                            )}
                            <Table
                                size="small"
                                rowKey={(o, i) => `${o.id}-${i}`}
                                dataSource={detail.offers}
                                pagination={{ pageSize: 15 }}
                                scroll={{ x: true }}
                                columns={[
                                    {
                                        title: 'Supplier',
                                        dataIndex: 'supplier_code',
                                        width: 110,
                                        filters: _.uniq(
                                            detail.offers.map(o => o.supplier_code)
                                        ).map(s => ({ text: s, value: s })),
                                        onFilter: (v, o) => o.supplier_code === v,
                                        render: c => <Tag>{c}</Tag>
                                    },
                                    {
                                        title: 'Board',
                                        width: 140,
                                        filters: _.uniq(
                                            detail.offers.map(
                                                o =>
                                                    (o.rooms &&
                                                        o.rooms[0] &&
                                                        o.rooms[0].board_basis) ||
                                                    ''
                                            )
                                        )
                                            .filter(Boolean)
                                            .map(b => ({ text: b, value: b })),
                                        onFilter: (v, o) =>
                                            ((o.rooms && o.rooms[0] && o.rooms[0].board_basis) ||
                                                '') === v,
                                        render: o =>
                                            (o.rooms && o.rooms[0] && o.rooms[0].board_basis) || ''
                                    },
                                    {
                                        title: 'Rooms',
                                        ellipsis: true,
                                        render: o =>
                                            (o.rooms || []).map(r => r.room_name).join(' + ')
                                    },
                                    {
                                        title: 'Price',
                                        width: 130,
                                        sorter: (a, b) => Number(a.price) - Number(b.price),
                                        defaultSortOrder: 'ascend',
                                        render: o => (
                                            <Text strong>
                                                {Number(o.price).toLocaleString()} {o.currency}
                                            </Text>
                                        )
                                    },
                                    {
                                        title: 'Refundable',
                                        width: 170,
                                        filters: [
                                            { text: 'refundable', value: 'ref' },
                                            { text: 'non-refundable', value: 'nonref' }
                                        ],
                                        onFilter: (v, o) =>
                                            v === 'nonref' ? !!o.non_refundable : !o.non_refundable,
                                        render: o =>
                                            o.non_refundable ? (
                                                <Tag color="orange">non-ref</Tag>
                                            ) : (
                                                <Tag color="green">
                                                    {o.free_cancellation_deadline
                                                        ? `free till ${String(
                                                              o.free_cancellation_deadline
                                                          ).slice(0, 10)}`
                                                        : 'refundable'}
                                                </Tag>
                                            )
                                    },
                                    {
                                        title: '',
                                        width: 110,
                                        render: o => (
                                            <Button
                                                type="primary"
                                                size="small"
                                                onClick={() => doPrebook(o)}
                                            >
                                                Prebook
                                            </Button>
                                        )
                                    }
                                ]}
                            />
                        </>
                    )}
                </Spin>
            </Drawer>

            <Modal
                visible={prebookState.open}
                title="Prebook result"
                footer={null}
                width={660}
                onCancel={() => setPrebookState(s => ({ ...s, open: false }))}
            >
                <Spin spinning={prebookState.loading}>
                    {prebookState.result && (
                        <PrebookResult
                            result={prebookState.result}
                            offerId={prebookState.offerId}
                        />
                    )}
                </Spin>
            </Modal>
        </div>
    )
}
